// ** Redux Imports
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import axios from 'axios';
import { isNil } from 'lodash-es';

import { getErrorMessage } from '@/utils/errors';

import { RootState } from '../index';

const DEBUG = false;
// A sibling of `/casino-instance/list` (see `casino-instance.ts`) on the SAME
// `userApiUrl` base, so NO new `window.config` / Helm `configmap.yaml` key is
// needed. The path resolves on test/prod from the already-registered key, and in
// dev from `public/assets/config/ini.js`. See docs/api.owners-hub.domain-info.md.
const apiUrl = window.config.userApiUrl + '/casino-instance/domain-info';

type GenericError = {
  rejectValue: {
    error: string;
  };
};

const getAuthHeaders = (state: RootState): Record<string, string> | null => {
  const authToken = state.authentication.access_token;
  if (isNil(authToken)) return null;
  return { Authorization: authToken };
};

/* ---------- API contract ----------

The web address(es) a casino instance (brand) is reachable on, plus the registrar /
status / DNS scaffolding the Settings → Website Domain section renders. One POST,
keyed in state by `casino_instance_id` so several brands can be loaded at once.

  POST {userApiUrl}/casino-instance/domain-info
       { casino_instance_id }
       → the brand's domain record(s). Stored RAW (`unknown`) and shaped by the
         page's `resolveCasinoDomains` adapter (see
         `src/nova/pages/casino-settings/domains.ts`), so a change to the
         response shape never touches this slice, only the adapter.

Auth: the access token in the `Authorization` header (raw, no `Bearer ` prefix).

Error note (verified live on `micros-spark-test1`): a brand with no domain on
record returns **HTTP 500** with `{ "error": { "code": "DOMAIN_NOT_FOUND", "message": … } }`
(NOT 404), and an upstream registrar failure returns **HTTP 500** with a free-form
`{ "error": <string>, "errorUUID", "stack" }`. Both surface here as a rejected
thunk (the error string is `[DOMAIN_NOT_FOUND]: …` for the former, see
`getErrorMessage`). Because they are 500s (not 401/403) they do NOT trip the global
auth-interceptor, so a missing domain never logs the owner out. The consumer treats
any rejection / empty payload as "no managed domain record yet" and falls back to
its placeholder. A genuinely bad token still returns 401 and is handled globally.

⚠️ The SUCCESS payload shape is PROVISIONAL: it could not be observed on the test
environment (every available brand returns DOMAIN_NOT_FOUND, and the one brand with
a live domain hits an upstream 422). The adapter parses defensively across the
plausible field names. Confirm against a real success response and tighten it.
*/

/**
 * Raw `domain-info` payload, stored verbatim. The shape is intentionally `unknown`:
 * the page's `resolveCasinoDomains` adapter is the single place that interprets it,
 * so the slice is correct regardless of the eventual response shape.
 */
export type CasinoDomainInfo = unknown;

/* ---------- thunk ---------- */

/** POST the domain record(s) for a brand. State is keyed by `casino_instance_id`. */
export const fetchCasinoDomainInfo = createAsyncThunk<
  { casinoInstanceId: string; data: CasinoDomainInfo },
  { casinoInstanceId: string },
  GenericError
>('casinoDomainInfo/fetch', async ({ casinoInstanceId }, { rejectWithValue, getState }) => {
  try {
    const headers = getAuthHeaders(getState() as RootState);
    if (!headers) return rejectWithValue({ error: 'No authentication token found' });
    if (isNil(casinoInstanceId)) return rejectWithValue({ error: 'casinoInstanceId is required' });

    const response = await axios.post(apiUrl, { casino_instance_id: casinoInstanceId }, { headers });
    DEBUG && console.log('[CasinoDomainInfo Slice] fetch response', response);

    if (!isNil(response.data)) return { casinoInstanceId, data: response.data };
    return rejectWithValue({ error: "Couldn't fetch domain info" });
  } catch (error: any) {
    return rejectWithValue({ error: getErrorMessage(error) });
  }
});

/* ---------- slice state ---------- */

export interface CasinoDomainInfoState {
  /** Raw payloads keyed by `casino_instance_id`. */
  byCasinoInstance: Record<string, CasinoDomainInfo>;
  /** Per-instance request status (so brands load independently). */
  loadingByCasinoInstance: Record<string, boolean>;
  errorByCasinoInstance: Record<string, string | undefined>;
}

const initialState: CasinoDomainInfoState = {
  byCasinoInstance: {},
  loadingByCasinoInstance: {},
  errorByCasinoInstance: {},
};

export const casinoDomainInfoSlice = createSlice({
  name: 'casinoDomainInfo',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      // Keys derive from `action.meta.arg` on pending/rejected (no payload there)
      // and from the payload on fulfilled; all carry the same `casinoInstanceId`.
      .addCase(fetchCasinoDomainInfo.pending, (state, action) => {
        const id = action.meta.arg.casinoInstanceId;
        state.loadingByCasinoInstance[id] = true;
        state.errorByCasinoInstance[id] = undefined;
      })
      .addCase(fetchCasinoDomainInfo.fulfilled, (state, action) => {
        const id = action.payload.casinoInstanceId;
        state.byCasinoInstance[id] = action.payload.data;
        state.loadingByCasinoInstance[id] = false;
      })
      .addCase(fetchCasinoDomainInfo.rejected, (state, action) => {
        const id = action.meta.arg.casinoInstanceId;
        state.loadingByCasinoInstance[id] = false;
        state.errorByCasinoInstance[id] = action.payload?.error;
      });
  },
});

/* ---------- selectors ---------- */

/** The raw domain-info payload for a brand, or `undefined` if not fetched. */
export const selectCasinoDomainInfo = (
  state: RootState,
  casinoInstanceId: string | undefined | null,
): CasinoDomainInfo | undefined =>
  isNil(casinoInstanceId) ? undefined : state.casinoDomainInfo.byCasinoInstance[casinoInstanceId];

/** Whether the brand's domain-info request is in flight. */
export const selectCasinoDomainInfoLoading = (
  state: RootState,
  casinoInstanceId: string | undefined | null,
): boolean => (isNil(casinoInstanceId) ? false : !!state.casinoDomainInfo.loadingByCasinoInstance[casinoInstanceId]);

/** The brand's domain-info error (e.g. `[DOMAIN_NOT_FOUND]: …`), or `undefined`. */
export const selectCasinoDomainInfoError = (
  state: RootState,
  casinoInstanceId: string | undefined | null,
): string | undefined =>
  isNil(casinoInstanceId) ? undefined : state.casinoDomainInfo.errorByCasinoInstance[casinoInstanceId];

export default casinoDomainInfoSlice.reducer;
